/**
 * Timetables Service
 *
 * Manages school timetables - cyclical patterns of day templates.
 * Timetables define which day template is active on any given date.
 *
 * Key Concepts:
 * - Cycle Pattern: Ordered array of day template IDs [day1, day2, day3, ...] that repeats
 * - Cycle Length: Number of days in the cycle (4, 5, 6, or 7 typically)
 * - Start Date: Anchor point for calculating cycle position
 * - Date Overrides: Special days that break the normal cycle pattern
 *
 * Collection: schools/{schoolId}/timetables
 */

import {
  collection,
  doc,
  getDocs,
  getDoc,
  setDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  onSnapshot,
  writeBatch
} from 'firebase/firestore';
import { db } from '../firebase';
import { dayTemplatesService } from './dayTemplatesService';

// Cache for active timetable
let cachedTimetable = null;
let cacheSchoolId = null;
let cacheTimestamp = null;
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

class TimetablesService {
  constructor() {
    this.collectionName = 'timetables';
    this.listeners = new Map();
  }

  /**
   * Get collection reference for a specific school
   */
  getCollectionRef(schoolId) {
    if (!schoolId) {
      throw new Error('School ID is required for timetables operations');
    }
    return collection(db, 'schools', schoolId, this.collectionName);
  }

  /**
   * Clear cache when school changes
   */
  clearCache() {
    cachedTimetable = null;
    cacheSchoolId = null;
    cacheTimestamp = null;
  }

  /**
   * Get all timetables for a school
   */
  async getAll(schoolId) {
    if (!schoolId) {
      throw new Error('School ID is required');
    }

    try {
      const collectionRef = this.getCollectionRef(schoolId);
      const q = query(collectionRef, orderBy('createdAt', 'desc'));
      const snapshot = await getDocs(q);

      const timetables = [];
      snapshot.forEach((doc) => {
        timetables.push({ id: doc.id, ...doc.data() });
      });

      return timetables;
    } catch (error) {
      console.error('Error fetching timetables:', error);
      return [];
    }
  }

  /**
   * Get active timetable for a school (with caching)
   */
  async getActive(schoolId) {
    if (!schoolId) {
      throw new Error('School ID is required');
    }

    // Check cache
    if (cachedTimetable &&
        cacheSchoolId === schoolId &&
        cacheTimestamp &&
        Date.now() - cacheTimestamp < CACHE_DURATION) {
      return cachedTimetable;
    }

    try {
      const collectionRef = this.getCollectionRef(schoolId);
      const q = query(collectionRef, where('isActive', '==', true));
      const snapshot = await getDocs(q);

      if (snapshot.empty) {
        return null;
      }

      const timetables = snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
      const timetable = timetables.find(t => !this.hasDateScope(t))
        || [...timetables].sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0))[0];

      // Update cache
      cachedTimetable = timetable;
      cacheSchoolId = schoolId;
      cacheTimestamp = Date.now();

      return timetable;
    } catch (error) {
      console.error('Error fetching active timetable:', error);
      return null;
    }
  }

  /**
   * Get a single timetable by ID
   */
  async getById(schoolId, timetableId) {
    if (!schoolId || !timetableId) {
      throw new Error('School ID and Timetable ID are required');
    }

    try {
      const docRef = doc(this.getCollectionRef(schoolId), timetableId);
      const snapshot = await getDoc(docRef);

      if (!snapshot.exists()) {
        return null;
      }

      return { id: snapshot.id, ...snapshot.data() };
    } catch (error) {
      console.error('Error fetching timetable:', error);
      return null;
    }
  }

  /**
   * Subscribe to active timetable updates
   */
  subscribeActive(schoolId, callback) {
    if (!schoolId) {
      console.warn('No schoolId provided for subscription');
      callback(null);
      return () => {};
    }

    const collectionRef = this.getCollectionRef(schoolId);
    const q = query(collectionRef, where('isActive', '==', true));

    return onSnapshot(q,
      (snapshot) => {
        if (snapshot.empty) {
          this.clearCache();
          callback(null);
          return;
        }

        const timetables = snapshot.docs.map((snapshotDoc) => ({
          id: snapshotDoc.id,
          ...snapshotDoc.data(),
        }));
        const timetable = timetables.find((candidate) => !this.hasDateScope(candidate))
          || [...timetables].sort((left, right) => (
            (left.sortOrder || 0) - (right.sortOrder || 0)
          ))[0];

        // Update cache
        cachedTimetable = timetable;
        cacheSchoolId = schoolId;
        cacheTimestamp = Date.now();

        callback(timetable);
      },
      (error) => {
        console.error('Error in timetable subscription:', error);
        callback(null);
      }
    );
  }

  /**
   * Unsubscribe from real-time updates
   */
  unsubscribe(key) {
    const unsubscribe = this.listeners.get(key);
    if (unsubscribe) {
      unsubscribe();
      this.listeners.delete(key);
    }
  }

  /**
   * Create or update a timetable
   */
  /**
   * Create or update a timetable
   * Same pattern as dayTemplatesService.save() — straight merge, no side effects.
   * Use setActive() to toggle active status separately.
   */
  async save(schoolId, timetableData) {
    if (!schoolId) {
      throw new Error('School ID is required');
    }

    const { id, ...data } = timetableData;
    const timetableId = id || this.generateTimetableId(data.name);

    // Add timestamps
    const now = new Date();
    if (!id) {
      data.createdAt = now;
    }
    data.updatedAt = now;

    // Ensure startDate is a Firestore timestamp
    if (data.startDate && !(data.startDate instanceof Date)) {
      data.startDate = new Date(data.startDate);
    }

    const docRef = doc(this.getCollectionRef(schoolId), timetableId);
    await setDoc(docRef, data, { merge: true });

    // Clear cache
    this.clearCache();

    return { id: timetableId, ...data };
  }

  /**
   * Delete a timetable
   */
  async delete(schoolId, timetableId) {
    if (!schoolId || !timetableId) {
      throw new Error('School ID and Timetable ID are required');
    }

    const docRef = doc(this.getCollectionRef(schoolId), timetableId);
    await deleteDoc(docRef);

    // Clear cache
    this.clearCache();
  }

  /**
   * Deactivate all timetables for a school
   */
  async deactivateAll(schoolId) {
    const timetables = await this.getAll(schoolId);
    const batch = [];

    for (const timetable of timetables) {
      if (timetable.isActive) {
        const docRef = doc(this.getCollectionRef(schoolId), timetable.id);
        batch.push(setDoc(docRef, { isActive: false }, { merge: true }));
      }
    }

    await Promise.all(batch);
    this.clearCache();
  }

  /**
   * Set a timetable as active
   */
  async setActive(schoolId, timetableId) {
    // Deactivate all others first
    await this.deactivateAll(schoolId);

    // Activate this one
    const docRef = doc(this.getCollectionRef(schoolId), timetableId);
    await setDoc(docRef, { isActive: true, updatedAt: new Date() }, { merge: true });

    this.clearCache();
  }

  /**
   * Get current day template for a given date
   * This is the core calculation that determines which day template is active
   */
  async getCurrentDayTemplate(schoolId, date = new Date()) {
    const timetable = await this.getActive(schoolId);
    if (!timetable) {
      return null;
    }

    // Check for date override first
    const dateOverride = this.getDateOverride(timetable, date);
    if (dateOverride) {
      return dayTemplatesService.getById(schoolId, dateOverride);
    }

    // Calculate cycle position
    const cycleDay = this.getCycleDay(date, timetable.startDate, timetable.cycleLength, timetable.daysOfWeek);
    const templateId = timetable.cyclePattern[cycleDay];

    if (!templateId) {
      return null;
    }

    return dayTemplatesService.getById(schoolId, templateId);
  }

  /**
   * Get day template ID for a specific date (without fetching full template)
   */
  async getDayTemplateId(schoolId, date = new Date()) {
    const timetable = await this.getActive(schoolId);
    if (!timetable) {
      return null;
    }

    // Check for date override first
    const dateOverride = this.getDateOverride(timetable, date);
    if (dateOverride) {
      return dateOverride;
    }

    // Calculate cycle position
    const cycleDay = this.getCycleDay(date, timetable.startDate, timetable.cycleLength, timetable.daysOfWeek);
    return timetable.cyclePattern[cycleDay];
  }

  /**
   * Calculate which day of the cycle a given date falls on.
   * Only counts school days (matching daysOfWeek) — skips weekends/non-school days.
   *
   * @param {Date} date - The date to check
   * @param {Date} startDate - The cycle start date (anchor point, must be a school day)
   * @param {number} cycleLength - Number of days in the cycle
   * @param {string[]} [daysOfWeek] - School days e.g. ['monday','tuesday','wednesday','thursday','friday']
   * @returns {number|null} - Index in the cycle pattern (0 to cycleLength-1), or null if not a school day
   */
  getCycleDay(date, startDate, cycleLength, daysOfWeek) {
    const dayNames = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];

    // Default to weekdays if not specified
    const schoolDays = daysOfWeek?.length
      ? daysOfWeek
      : ['monday', 'tuesday', 'wednesday', 'thursday', 'friday'];

    // Build a set of day indices (0=Sun..6=Sat) for fast lookup
    const schoolDayIndices = new Set(schoolDays.map(d => dayNames.indexOf(d)));
    const schoolDaysPerWeek = schoolDayIndices.size;
    if (schoolDaysPerWeek === 0) return null;

    const normalizedDate = new Date(date);
    normalizedDate.setHours(0, 0, 0, 0);

    const normalizedStart = new Date(startDate?.toDate ? startDate.toDate() : startDate);
    normalizedStart.setHours(0, 0, 0, 0);

    // Snap startDate forward to the next school day if it falls on a non-school day
    while (!schoolDayIndices.has(normalizedStart.getDay())) {
      normalizedStart.setDate(normalizedStart.getDate() + 1);
    }

    // Not a school day? Return null
    if (!schoolDayIndices.has(normalizedDate.getDay())) {
      return null;
    }

    // Count school days between start (exclusive) and date (inclusive) using math
    const totalDays = Math.round((normalizedDate - normalizedStart) / (1000 * 60 * 60 * 24));
    const forward = totalDays >= 0;
    const absDays = Math.abs(totalDays);

    // Full weeks + remainder days
    const fullWeeks = Math.floor(absDays / 7);
    const remainderDays = absDays % 7;

    // Count school days in the remainder by walking only 0-6 steps
    let remainderSchoolDays = 0;
    const startDow = normalizedStart.getDay();
    for (let i = 1; i <= remainderDays; i++) {
      const dow = forward
        ? (startDow + i) % 7
        : ((startDow - i) % 7 + 7) % 7;
      if (schoolDayIndices.has(dow)) remainderSchoolDays++;
    }

    const schoolDayCount = fullWeeks * schoolDaysPerWeek + remainderSchoolDays;

    if (forward) {
      return schoolDayCount % cycleLength;
    } else {
      const offset = schoolDayCount % cycleLength;
      return offset === 0 ? 0 : cycleLength - offset;
    }
  }

  /**
   * Check if a specific date has an override
   * @param {Object} timetable - The timetable object
   * @param {Date} date - The date to check
   * @returns {string|null} - Day template ID if override exists, null otherwise
   */
  getDateOverride(timetable, date) {
    if (!timetable.dateOverrides) {
      return null;
    }

    // Format date as YYYY-MM-DD
    const dateKey = this.formatDateKey(date);
    return timetable.dateOverrides[dateKey] || null;
  }

  /**
   * Add a date override to a timetable
   */
  async addDateOverride(schoolId, timetableId, date, dayTemplateId) {
    const timetable = await this.getById(schoolId, timetableId);
    if (!timetable) {
      throw new Error('Timetable not found');
    }

    const dateKey = this.formatDateKey(date);
    const dateOverrides = timetable.dateOverrides || {};
    dateOverrides[dateKey] = dayTemplateId;

    await this.save(schoolId, {
      ...timetable,
      dateOverrides
    });
  }

  /**
   * Remove a date override from a timetable
   */
  async removeDateOverride(schoolId, timetableId, date) {
    const timetable = await this.getById(schoolId, timetableId);
    if (!timetable) {
      throw new Error('Timetable not found');
    }

    const dateKey = this.formatDateKey(date);
    const dateOverrides = timetable.dateOverrides || {};
    delete dateOverrides[dateKey];

    await this.save(schoolId, {
      ...timetable,
      dateOverrides
    });
  }

  /**
   * Get cycle preview for a date range
   * @returns {Array} - Array of {date, cycleDay, templateId, isOverride}
   */
  async getCyclePreview(schoolId, startDate, endDate) {
    const timetable = await this.getActive(schoolId);
    if (!timetable) {
      return [];
    }

    const preview = [];
    const currentDate = new Date(startDate);
    currentDate.setHours(0, 0, 0, 0);

    const end = new Date(endDate);
    end.setHours(0, 0, 0, 0);

    while (currentDate <= end) {
      const dateKey = this.formatDateKey(currentDate);
      const override = this.getDateOverride(timetable, currentDate);
      const cycleDay = this.getCycleDay(currentDate, timetable.startDate, timetable.cycleLength, timetable.daysOfWeek);
      const templateId = override || timetable.cyclePattern[cycleDay];

      preview.push({
        date: new Date(currentDate),
        dateKey,
        cycleDay,
        templateId,
        isOverride: !!override
      });

      currentDate.setDate(currentDate.getDate() + 1);
    }

    return preview;
  }

  /**
   * Validate timetable data
   */
  async validateTimetable(schoolId, timetable) {
    const errors = [];

    if (!timetable.name) {
      errors.push('Timetable name is required');
    }

    if (!timetable.cyclePattern || !Array.isArray(timetable.cyclePattern)) {
      errors.push('Cycle pattern must be an array');
    } else if (timetable.cyclePattern.length === 0) {
      errors.push('Cycle pattern must have at least one day template');
    }

    if (!timetable.cycleLength || timetable.cycleLength < 1) {
      errors.push('Cycle length must be at least 1');
    }

    if (timetable.cyclePattern && timetable.cycleLength &&
        timetable.cyclePattern.length !== timetable.cycleLength) {
      errors.push('Cycle pattern length must match cycle length');
    }

    if (!timetable.startDate) {
      errors.push('Start date is required');
    }

    // Validate that all referenced day templates exist
    if (timetable.cyclePattern && schoolId) {
      const templates = await dayTemplatesService.getAll(schoolId);
      const templateIds = new Set(templates.map(t => t.id));

      timetable.cyclePattern.forEach((templateId, index) => {
        if (templateId && !templateIds.has(templateId)) {
          errors.push(`Day template "${templateId}" at position ${index + 1} does not exist`);
        }
      });
    }

    return errors;
  }

  /**
   * Subscribe to real-time updates for ALL timetables
   */
  subscribe(schoolId, callback) {
    if (!schoolId) {
      console.warn('No schoolId provided for subscription');
      callback([]);
      return () => {};
    }

    this.unsubscribe(`${schoolId}-all`);

    const collectionRef = this.getCollectionRef(schoolId);
    const q = query(collectionRef, orderBy('createdAt', 'asc'));

    const unsubscribe = onSnapshot(q,
      (snapshot) => {
        const timetables = [];
        snapshot.forEach((doc) => {
          timetables.push({ id: doc.id, ...doc.data() });
        });
        callback(timetables);
      },
      (error) => {
        console.error('Error in timetables subscription:', error);
        callback([]);
      }
    );

    this.listeners.set(`${schoolId}-all`, unsubscribe);
    return unsubscribe;
  }

  /**
   * Clone (duplicate) a timetable
   */
  async clone(schoolId, timetableId) {
    const timetable = await this.getById(schoolId, timetableId);
    if (!timetable) {
      throw new Error('Timetable not found');
    }

    const data = { ...timetable };
    delete data.id;
    delete data.createdAt;
    delete data.updatedAt;
    delete data.isActive;

    const cloned = {
      ...data,
      name: `${data.name} (Copy)`,
      isActive: false,
    };

    return this.save(schoolId, cloned);
  }

  /**
   * Update sort orders for multiple timetables (drag-and-drop reorder)
   */
  async updateSortOrders(schoolId, updates) {
    if (!schoolId || !updates?.length) return;

    const batch = writeBatch(db);
    const collectionRef = this.getCollectionRef(schoolId);

    updates.forEach(({ id, sortOrder }) => {
      const docRef = doc(collectionRef, id);
      batch.update(docRef, { sortOrder, updatedAt: new Date() });
    });

    await batch.commit();
    this.clearCache();
  }

  /**
   * Helper: Format date as YYYY-MM-DD for override keys
   */
  formatDateKey(date) {
    const d = new Date(date);
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  hasDateScope(timetable) {
    return Boolean(timetable?.dateRangeStart || timetable?.dateRangeEnd);
  }

  /**
   * Helper: Generate timetable ID from name
   */
  generateTimetableId(name) {
    return name.toLowerCase()
      .replace(/\s+/g, '-')
      .replace(/[^a-z0-9-]/g, '');
  }

  /**
   * Create default timetable for a school
   */
  async createDefault(schoolId) {
    // Ensure default day template exists
    const templates = await dayTemplatesService.getAll(schoolId);
    let defaultTemplateId = templates.find(t => t.id === 'normal-day')?.id;

    if (!defaultTemplateId) {
      const defaultTemplate = await dayTemplatesService.save(schoolId, {
        ...dayTemplatesService.getDefaultTemplates()[0]
      });
      defaultTemplateId = defaultTemplate.id;
    }

    // Create 5-day cycle timetable
    const timetable = {
      name: 'Main Timetable',
      description: '5-day cycle timetable',
      cyclePattern: [
        defaultTemplateId,
        defaultTemplateId,
        defaultTemplateId,
        defaultTemplateId,
        defaultTemplateId
      ],
      cycleLength: 5,
      startDate: new Date(),
      isActive: true,
      dateOverrides: {}
    };

    return this.save(schoolId, timetable);
  }
}

// Export singleton instance
export const timetablesService = new TimetablesService();
